/**
 * SideRail.tsx -- Left-side navigation rail for workspace-scoped routes.
 *
 * Default width 240px, collapsed 56px. Workspace switcher at top (uses Dropdown).
 * Initiative navigation below when initiativeId is provided:
 *   Overview, Plan, Tasks, Runs, Briefs & Assets, Settings
 *   Each link with active-state border-left: 2px solid var(--color-primary)
 *   + background: var(--bg2)
 * Recents cluster: first 5 rows from useRecentRuns in mono-label typography.
 * Collapse button at bottom; persists to localStorage under
 * key `capicua.side-rail-collapsed`.
 * Below 1024px viewport: renders as 48px launcher button only;
 * clicking opens the rail as a fixed overlay drawer.
 *
 * Consumes only CSS custom-property references (var(--*)) for colour,
 * font, spacing, radius, and focus-ring.
 *
 * @see specs/integration-plan.md Section 4.4
 */
/* global window, localStorage */
import React, { useState, useCallback, useMemo, useEffect } from "react";
import { useHashLocation } from "../../lib/router";
import { useCurrentWorkspace } from "../../api/hooks/useCurrentWorkspace";
import { useRecentRuns } from "../../api/hooks/useRecentRuns";
import { usePrefetchRoute } from "../../lib/usePrefetchRoute";
import { getPrefetchFactory } from "../../lib/routePrefetch";
import { Dropdown } from "../primitives";
import type { CSSProperties } from "react";
import { useT } from "../../i18n/useT";

const PRIMARY_COLOR = "var(--sora)";

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export interface SideRailProps {
  workspaceSlug: string;
  initiativeId?: string;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const STORAGE_KEY = "capicua.side-rail-collapsed";
const DEFAULT_WIDTH = 240;
const COLLAPSED_WIDTH = 56;
const MAX_RECENTS = 5;
const NARROW_QUERY = "(max-width: 1023px)";

// ---------------------------------------------------------------------------
// Initiative nav items
// ---------------------------------------------------------------------------

interface NavItem {
  label: string;
  segment: string;
}

const INITIATIVE_NAV_ITEMS: NavItem[] = [
  { label: "Overview", segment: "overview" },
  { label: "Plan", segment: "plan" },
  { label: "Tasks", segment: "tasks" },
  { label: "Runs", segment: "runs" },
  { label: "Briefs & Assets", segment: "briefs" },
  { label: "Settings", segment: "settings" },
];

// ---------------------------------------------------------------------------
// Styles (CSS custom-property references only)
// ---------------------------------------------------------------------------

const railBaseStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  height: "100%",
  background: "var(--bg1)",
  borderRight: "1px solid var(--color-border)",
  overflow: "hidden",
  transition: "width 150ms ease",
};

const workspaceSwitcherStyle: CSSProperties = {
  padding: "var(--space-md) var(--space-sm)",
  borderBottom: "1px solid var(--color-border)",
};

const switcherButtonStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "var(--space-xs)",
  width: "100%",
  padding: "var(--space-xs) var(--space-sm)",
  background: "transparent",
  border: "none",
  borderRadius: "var(--radius-sm)",
  cursor: "pointer",
  fontFamily: "var(--font-ui)",
  fontSize: "var(--fs-body)",
  color: "var(--fg1)",
  textAlign: "left" as const,
};

const navSectionStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  padding: "var(--space-sm) 0",
  flex: 1,
  overflow: "auto",
};

const navLinkBaseStyle: CSSProperties = {
  display: "block",
  padding: "var(--space-xs) var(--space-lg)",
  fontFamily: "var(--font-ui)",
  fontSize: "var(--fs-body)",
  color: "var(--fg2)",
  textDecoration: "none",
  cursor: "pointer",
};

const recentsHeadingStyle: CSSProperties = {
  fontFamily: "var(--font-ui)",
  fontSize: "var(--fs-micro)",
  color: "var(--fg3)",
  textTransform: "uppercase" as const,
  letterSpacing: "0.05em",
  padding: "var(--space-sm) var(--space-lg)",
  margin: 0,
};

const recentLinkStyle: CSSProperties = {
  display: "block",
  padding: "var(--space-xxs) var(--space-lg)",
  fontFamily: "var(--font-mono)",
  fontSize: "var(--fs-micro)",
  color: "var(--fg3)",
  textDecoration: "none",
  whiteSpace: "nowrap" as const,
  overflow: "hidden",
  textOverflow: "ellipsis",
};

const collapseBarStyle: CSSProperties = {
  padding: "var(--space-sm)",
  borderTop: "1px solid var(--color-border)",
  display: "flex",
  justifyContent: "center",
};

const collapseButtonStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "32px",
  height: "32px",
  background: "transparent",
  border: "none",
  borderRadius: "var(--radius-sm)",
  cursor: "pointer",
  color: "var(--fg3)",
  fontSize: "var(--fs-body)",
};

const launcherStyle: CSSProperties = {
  width: "48px",
  height: "48px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "var(--bg1)",
  border: "1px solid var(--color-border)",
  borderRadius: "var(--radius-sm)",
  cursor: "pointer",
  color: "var(--fg2)",
  fontSize: "var(--fs-body)",
};

const overlayStyle: CSSProperties = {
  position: "fixed",
  top: "0",
  bottom: "0",
  left: "0",
  zIndex: 100,
  width: `${DEFAULT_WIDTH}px`,
  background: "var(--bg1)",
  borderRight: "1px solid var(--color-border)",
  display: "flex",
  flexDirection: "column",
  boxShadow: "4px 0 16px rgba(0,0,0,0.1)",
};

const overlayBackdropStyle: CSSProperties = {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 99,
  background: "rgba(0,0,0,0.2)",
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function readCollapsedState(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

function writeCollapsedState(collapsed: boolean): void {
  try {
    if (collapsed) {
      localStorage.setItem(STORAGE_KEY, "true");
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // Silently ignore storage failures
  }
}

/**
 * useNarrowViewport -- Reactive viewport subscription (FR-007 — T306).
 *
 * Subscribes to MediaQueryList.change events so the side-rail switches
 * between expanded and launcher state without a route change when the
 * viewport crosses the 1024px boundary mid-session.
 */
function useNarrowViewport(): boolean {
  const [isNarrow, setIsNarrow] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia(NARROW_QUERY).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined") return;

    const mql = window.matchMedia(NARROW_QUERY);

    function handleChange(e: MediaQueryListEvent) {
      setIsNarrow(e.matches);
    }

    // Subscribe to change events
    mql.addEventListener("change", handleChange);

    // Sync initial state (in case it changed between useState init and effect)
    setIsNarrow(mql.matches);

    return () => {
      mql.removeEventListener("change", handleChange);
    };
  }, []);

  return isNarrow;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function WorkspaceSwitcher({
  collapsed,
}: {
  collapsed: boolean;
}) {
  const t = useT();
  const workspace = useCurrentWorkspace();
  const emptyName = "";
  const name = workspace.status === "ready" ? workspace.data?.name : emptyName;
  const initial = name ? name.charAt(0).toUpperCase() : "W";

  if (collapsed) {
    return (
      <div style={workspaceSwitcherStyle}>
        <button
          type="button"
          role="button"
          style={{
            ...switcherButtonStyle,
            justifyContent: "center",
            padding: "var(--space-xs)",
          }}
          aria-label={`Workspace: ${name || t("component.loading")}`}
        >
          {initial}
        </button>
      </div>
    );
  }

  return (
    <div style={workspaceSwitcherStyle}>
      <Dropdown
        trigger={
          <button type="button" role="button" style={switcherButtonStyle}>
            {name || t("component.loading-ellipsis")}
          </button>
        }
        items={[]}
        ariaLabel="Workspace switcher"
      />
    </div>
  );
}

function PrefetchSideLink({
  href,
  isActive,
  children,
}: {
  href: string;
  isActive: boolean;
  children: React.ReactNode;
}) {
  const factory = getPrefetchFactory(href);
  const prefetch = usePrefetchRoute(factory);

  return (
    <a
      href={href}
      style={{
        ...navLinkBaseStyle,
        borderLeftWidth: "2px",
        borderLeftStyle: "solid",
        borderLeftColor: isActive
          ? PRIMARY_COLOR
          : "transparent",
        background: isActive ? "var(--bg2)" : "transparent",
        color: isActive ? "var(--fg1)" : "var(--fg2)",
      }}
      {...(isActive ? { "aria-current": "page" as const } : {})}
      onPointerEnter={prefetch.onPointerEnter}
      onPointerLeave={prefetch.onPointerLeave}
      onFocus={prefetch.onFocus}
      onBlur={prefetch.onBlur}
    >
      {children}
    </a>
  );
}

function InitiativeNav({
  workspaceSlug,
  initiativeId,
}: {
  workspaceSlug: string;
  initiativeId: string;
}) {
  const [location] = useHashLocation();
  const basePath = `/workspace/${workspaceSlug}/initiative/${initiativeId}`;

  // Determine which segment is active; default to "overview" if none match
  const activeSegment = useMemo(() => {
    for (const item of INITIATIVE_NAV_ITEMS) {
      if (location === `${basePath}/${item.segment}`) {
        return item.segment;
      }
    }
    // Default to overview when at the base path or no segment matches
    return "overview";
  }, [location, basePath]);

  return (
    <nav data-testid="initiative-nav" style={navSectionStyle}>
      {INITIATIVE_NAV_ITEMS.map((item) => {
        const href = `#${basePath}/${item.segment}`;
        const isActive = item.segment === activeSegment;

        return (
          <PrefetchSideLink
            key={item.segment}
            href={href}
            isActive={isActive}
          >
            {item.label}
          </PrefetchSideLink>
        );
      })}
    </nav>
  );
}

function RecentsCluster({
  workspaceSlug,
}: {
  workspaceSlug: string;
}) {
  const recentRuns = useRecentRuns(workspaceSlug);
  const items = recentRuns.status === "ready" ? recentRuns.data : [];
  const visibleItems = useMemo(() => items.slice(0, MAX_RECENTS), [items]);

  return (
    <div data-testid="side-rail-recents" style={{ padding: "var(--space-xs) 0" }}>
      <h3 style={recentsHeadingStyle}>Recents</h3>
      {visibleItems.map((run) => (
        <a key={run.id} href={run.href} style={recentLinkStyle}>
          {run.label}
        </a>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Rail content (shared between desktop and overlay modes)
// ---------------------------------------------------------------------------

function RailContent({
  workspaceSlug,
  initiativeId,
  collapsed,
  onToggleCollapse,
}: {
  workspaceSlug: string;
  initiativeId?: string;
  collapsed: boolean;
  onToggleCollapse: () => void;
}) {
  return (
    <>
      <WorkspaceSwitcher collapsed={collapsed} />

      {!collapsed && initiativeId && (
        <InitiativeNav
          workspaceSlug={workspaceSlug}
          initiativeId={initiativeId}
        />
      )}

      {!collapsed && (
        <RecentsCluster workspaceSlug={workspaceSlug} />
      )}

      <div style={{ flex: 1 }} />

      <div style={collapseBarStyle}>
        <button
          type="button"
          style={collapseButtonStyle}
          onClick={onToggleCollapse}
          aria-label={collapsed ? "Expand side rail" : "Collapse side rail"}
        >
          {collapsed ? "\u25B6" : "\u25C0"}
        </button>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function SideRail({
  workspaceSlug,
  initiativeId,
}: SideRailProps): React.ReactElement | null {
  const isNarrow = useNarrowViewport();
  const [collapsed, setCollapsed] = useState(readCollapsedState);
  const [overlayOpen, setOverlayOpen] = useState(false);

  const toggleCollapse = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      writeCollapsedState(next);
      return next;
    });
  }, []);

  const openOverlay = useCallback(() => {
    setOverlayOpen(true);
  }, []);

  const closeOverlay = useCallback(() => {
    setOverlayOpen(false);
  }, []);

  // Close overlay when viewport switches from narrow to wide
  useEffect(() => {
    if (!isNarrow && overlayOpen) {
      setOverlayOpen(false);
    }
  }, [isNarrow, overlayOpen]);

  const width = collapsed ? COLLAPSED_WIDTH : DEFAULT_WIDTH;

  // FR-007 — viewport-driven conditional render. Only ONE variant mounts
  // at a time (driven by useNarrowViewport's matchMedia subscription), so
  // both jsdom tests and production avoid the dual-tree ambiguity that
  // CSS-only visibility gating would produce.
  if (!isNarrow) {
    return (
      <div className="sideRail__expanded">
        <aside
          data-testid="side-rail"
          style={{ ...railBaseStyle, width: `${width}px` }}
          role="navigation"
          aria-label="Workspace navigation"
        >
          <RailContent
            workspaceSlug={workspaceSlug}
            initiativeId={initiativeId}
            collapsed={collapsed}
            onToggleCollapse={toggleCollapse}
          />
        </aside>
      </div>
    );
  }

  // Narrow viewport (<1024px): launcher + overlay drawer.
  return (
    <div className="sideRail__launcher">
        <button
          type="button"
          data-testid="side-rail-launcher"
          style={launcherStyle}
          onClick={openOverlay}
          aria-label="Open navigation"
        >
          {"\u2630"}
        </button>
        {overlayOpen && (
          <>
            <div
              style={overlayBackdropStyle}
              onClick={closeOverlay}
              aria-hidden="true"
            />
            <aside
              data-testid="side-rail-overlay"
              style={overlayStyle}
              role="navigation"
              aria-label="Workspace navigation"
            >
              <RailContent
                workspaceSlug={workspaceSlug}
                initiativeId={initiativeId}
                collapsed={false}
                onToggleCollapse={closeOverlay}
              />
            </aside>
          </>
        )}
      </div>
  );
}

export default SideRail;
